import React, { PureComponent, createRef, Fragment } from 'react';
import './GeneralPrediction.css';
import 'leaflet/dist/leaflet.css';
import PropTypes from 'prop-types';
import L, { icon } from 'leaflet';
import omnivore from '@mapbox/leaflet-omnivore';
import * as icons from '../../img';
import {
    limitsComarquesAmbMar,
    baseTiles,
    suffixUrlTiles,
} from '../../utils/urls';
import DayBox from '../DayBox/DayBox';
import {
    DEFAULT_CONFIG,
    TENDENCIES_VALUES,
    CATALUNYA_CENTER,
    MAX_BOUNDS,
} from '../../utils/globals';
import { debounce, getTendencyIcon } from '../../utils/utils';

const dayBoxDimensions = { width: 94, height: 93 };
const comarcaLayerPointerOffset = 20;
const dayBoxOffset = 100;
const DEFAULT_ZOOM = 6.6;
const FIRST_ID_SEA_LAYER = 88;

export class GeneralPrediction extends PureComponent {
    state = {
        isDayBoxActive: false,
        comarcaId: 0,
        comarcaName: '',
        comarcaX: 0,
        comarcaY: 0,
        dayBoxConfig: {
            alternativeTitle: '',
            color: this.props.color || DEFAULT_CONFIG.color,
            language: this.props.language || DEFAULT_CONFIG.language,
            location: '080193',
            type: 'corporate',
            variables: ['est_cel', 'temp_max', 'temp_min', 'prob_prec'],
        },
        dayBoxData: {
            est_cel: {},
            prob_prec: {},
            temp_max: {},
            temp_max_unit: '',
            temp_min: {},
            temp_min_unit: '',
        },
    };

    mapContainer = createRef();
    zoom = this.props.zoom || DEFAULT_ZOOM;

    static propTypes = {
        comarquesCel: PropTypes.arrayOf(PropTypes.object),
        comarquesPrecipitacio: PropTypes.arrayOf(PropTypes.object),
        comarquesTempMax: PropTypes.arrayOf(PropTypes.object),
        comarquesTempMin: PropTypes.arrayOf(PropTypes.object),
        data: PropTypes.shape({
            simbols: PropTypes.object,
            tendencies: PropTypes.arrayOf(PropTypes.object),
        }),
        day: PropTypes.number,
        mapId: PropTypes.string,
        preview: PropTypes.bool,
        isFlexWrapped: PropTypes.bool,
        zoom: PropTypes.number,
    };

    style = {
        left: 0,
        top: 0,
    };

    render() {
        const {
            isDayBoxActive,
            comarcaX,
            comarcaY,
            comarcaName,
            dayBoxConfig,
            dayBoxVars,
            dayBoxData,
        } = this.state;
        const { mapId, preview, color, data } = this.props;
        
        return (
            <Fragment>
                {/*<div className="general-prediction__tendency">
                    <ul>
                        {this.props.data.tendencies.map((tendencia, index) => (
                            <li
                                key={index}
                                className={
                                    tendencia.valor === 0 ? 'scaleIt' : null
                                }
                            >
                                <img
                                    src={getTendencyIcon(tendencia)}
                                    alt={TENDENCIES_VALUES[tendencia.valor]}
                                />
                            </li>
                        ))}
                    </ul>
                </div>*/}          
                <div
                    id={mapId}
                    className="GeneralPrediction mc-leaflet-map"
                    style={{
                        height: !preview ? 'calc(100% - 26px)' : null,
                    }}
                    ref={this.mapContainer}
                >
                    {/* Overlay comarques */}
                    {isDayBoxActive && (
                        <div
                            className="mc-dayBox-comarca mc-widget"
                            style={{
                                left: comarcaX,
                                top: comarcaY,
                            }}
                        >                            
                            <div
                                className="mc-dayBox-comarca__header"
                                style={{ background: color }}
                            >
                                {comarcaName}
                            </div>
                            <DayBox
                                config={dayBoxConfig}
                                variables={dayBoxVars}
                                data={dayBoxData}
                                dayName={''}
                                index={1}
                                onlyOneDayBox={true}
                            />
                        </div>
                    )}
                </div>
            </Fragment>
        );
    }

    componentDidMount() {
        if (document.querySelector(`#${this.props.mapId}`)) {
            this.map = L.map(this.props.mapId, {
                center: [CATALUNYA_CENTER.latitude, CATALUNYA_CENTER.longitude],
                zoomSnap: 0.1,
                zoom: this.zoom,
                maxZoom: 9.6,
                minZoom: 5.6,
                maxBounds: MAX_BOUNDS,
            });

            
            this.generateTileLayer(`${baseTiles}${suffixUrlTiles}`, {}).addTo(
                this.map
            );

            this.comarquesTopojson = this.topojsonLayerGenerator().addTo(
                this.map
            );

            const zoom = this.calculateZoom();
            this.map.setZoom(zoom);
            this.paintIcons(Math.floor(zoom * 10) / 10);
            this.paintTendencies(this.props.data.tendencies, Math.floor(zoom * 10) / 10 );

            this.props.setParentZoomState(zoom);
        }

        window.addEventListener(
            'resize',
            debounce(() => {
                const zoom = this.calculateZoom();
                this.map.setZoom(zoom);
                this.map.removeLayer(this.layer);
                this.paintIcons(Math.floor(zoom * 10) / 10);
            })
        );

        this.map.on('zoomend', () => {
            const zoom = Math.floor(this.map.getZoom() * 10 / 10);
            this.map.removeLayer(this.layer);
            this.paintIcons(zoom);            
            this.map.removeLayer(this.tendenciesLayer);            
            this.paintTendencies(this.props.data.tendencies, zoom );
            this.props.setParentZoomState(zoom);
        });
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handler);
        if (this.customMarker && this.map) {
            this.map.removeLayer(this.customMarker);
        }
    }

    componentDidUpdate(prevProps) {
        const { color, language, day, data } = this.props;
        if (day !== prevProps.day) {
            this.map.removeLayer(this.layer);
            this.paintIcons(Math.floor(this.map.getZoom() * 10) / 10);            
            this.map.removeLayer(this.tendenciesLayer);
            this.paintTendencies(data.tendencies, Math.floor(this.map.getZoom() * 10) / 10);
        }

        if (color !== prevProps.color) {
            this.setState({
                dayBoxConfig: {
                    alternativeTitle: '',
                    color,
                    language,
                    location: '080193',
                    type: 'corporate',
                    variables: ['est_cel', 'temp_max', 'temp_min', 'prob_prec'],
                },
            });
        }
    }

    generateTileLayer(tilesUrl, layerOptions) {
        var newTile = L.tileLayer(tilesUrl, layerOptions);

        newTile.getTileUrl = function(tilePoint) {
            return L.Util.template(
                this._url,
                {
                    z: ('0' + tilePoint.z).slice(-2),
                    x: ('00' + tilePoint.x).slice(-3),
                    // For some reason Y axis is inverted, so we have to reorder tiles
                    y: (
                        '00' +
                        (this._globalTileRange.max.y - tilePoint.y)
                    ).slice(-3),
                },
                this.options
            );
        };

        return newTile;
    }

    topojsonLayerGenerator() {
        const layerStyles = L.geoJson(null, {
            style: () => {
                return {
                    opacity: 0,
                    fillOpacity: 0,
                    width: 0,
                    color: 'transparent',
                };
            },
        });

        // can't use arrow functions with omnivore ...
        const component = this;

        return omnivore
            .topojson(limitsComarquesAmbMar, null, layerStyles)
            .on('ready', function() {
                this.eachLayer(layer => {
                    layer.on('mouseover', layer => {
                        const {
                            IDComarca,
                            NOM_COMAR: nomComarca,
                        } = layer.sourceTarget.feature.properties;
                        const { x, y } = layer.containerPoint;

                        const mapDimensions = {
                            w: document.getElementById(component.props.mapId)
                                .offsetWidth,
                            h: document.getElementById(component.props.mapId)
                                .offsetHeight,
                        };

                        let comarcaX = x + comarcaLayerPointerOffset;
                        let comarcaY = y + comarcaLayerPointerOffset;

                        // We want comarques overlays always inside of the container
                        if (
                            x +
                                dayBoxDimensions.width +
                                comarcaLayerPointerOffset >
                            mapDimensions.w
                        ) {
                            comarcaX = x - dayBoxOffset;
                        }
                        if (
                            y +
                                dayBoxDimensions.height +
                                comarcaLayerPointerOffset >
                            mapDimensions.h
                        ) {
                            comarcaY = y - dayBoxOffset;
                        }

                        if (component.isNotSeaLayer(IDComarca)) {
                            component.setState({
                                comarcaId: IDComarca,
                                comarcaName: nomComarca,
                                comarcaX: comarcaX,
                                comarcaY: comarcaY,
                            });
                            component.setDayBoxData();
                        }
                    });

                    layer.on('mouseout', () => {
                        component.setState({ isDayBoxActive: false });
                    });
                });
            });
    }

    calculaIconaSize(zoom){

        let iconsSize;
        const zoomCases = [6, 6.5, 7, 8, 9];
        const iconSizes = [24, 24, 32, 48, 70];

        switch (true) {
            case zoom < zoomCases[1]:
                iconsSize = iconSizes[0];
                break;
            case zoom < zoomCases[2]:
                iconsSize = iconSizes[1];
                break;
            case zoom < zoomCases[3]:
                iconsSize = iconSizes[2];
                break;
            case zoom < zoomCases[4]:
                iconsSize = iconSizes[3];
                break;
            case zoom >= zoomCases[4]:
                iconsSize = iconSizes[4];
                break;
            default:
                iconsSize = iconSizes[1];
        }

        return iconsSize;

    }

    paintTendencies(tendencies, zoom){

        let iconsSize = this.calculaIconaSize(zoom);
        const posicions = [
            [[40.7, 2.4]],
            [[40.7, 2.1], [40.7, 2.4]]
        ];
        let markersTendencia = [];         

        tendencies.forEach((tendencia, index) => {
            
            const myIcon = L.icon({
                iconUrl: getTendencyIcon(tendencia),
                iconSize: [iconsSize, iconsSize],
                iconAnchor: [iconsSize / 2, iconsSize / 2],
            });
            markersTendencia.push(L.marker(posicions[tendencies.length - 1][index], { icon: myIcon }).addTo(this.map));
        });
        
        this.tendenciesLayer = L.layerGroup(markersTendencia).addTo(this.map);
    }

    paintIcons(zoom) {
        let markers = []; // just a container for all icons

        const { estatDelCel, estatDelMar, vent } = this.props.data.simbols;

        markers = markers.concat(
            this.produceIcons(markers, estatDelCel, 'est_cel_', zoom),
            this.produceIcons(markers, estatDelMar, 'est_mar_', zoom),
            this.produceIcons(markers, vent, 'vent_', zoom)
        );

        this.layer = L.layerGroup(markers).addTo(this.map);
    }

    

    produceIcons(markers, data, iconPrefix, zoom) {
        
        let iconsSize = this.calculaIconaSize(zoom);
        /*let iconsSize;
        const zoomCases = [6, 6.5, 7, 8, 9];
        const iconSizes = [18, 24, 32, 48, 70];

        switch (true) {
            case zoom < zoomCases[1]:
                iconsSize = iconSizes[0];
                break;
            case zoom < zoomCases[2]:
                iconsSize = iconSizes[1];
                break;
            case zoom < zoomCases[3]:
                iconsSize = iconSizes[2];
                break;
            case zoom < zoomCases[4]:
                iconsSize = iconSizes[3];
                break;
            case zoom >= zoomCases[4]:
                iconsSize = iconSizes[4];
                break;
            default:
                iconsSize = iconSizes[1];
        }*/

        data.forEach(item => {
            const icon = icons[`${iconPrefix}${item.codi || item.velocitat}`];
            if (icon) {
                markers.push(
                    L.marker(
                        [item.coordenades.latitud, item.coordenades.longitud],
                        {
                            icon: new L.DivIcon({
                                html: `<img src="${icon}" width="${iconsSize}" height="${iconsSize}" style="${
                                    item.direccio
                                        ? `transform: rotate(${
                                              item.direccio
                                          }deg); -ms-transform: rotate(${
                                              item.direccio
                                          }deg); -webkit-transform: rotate(${
                                              item.direccio
                                          }deg);`
                                        : ''
                                } " /> ${
                                    item.beaufort
                                        ? `<span>${item.beaufort}</span>`
                                        : ''
                                }`,
                                iconAnchor: [iconsSize / 2, iconsSize / 2],
                            }),
                        }
                    )
                );
            }
        });
        return markers;
    }

    // Feed state with current comarca data in order to render dayBox
    setDayBoxData = () => {
        const {
            comarquesCel,
            comarquesPrecipitacio,
            comarquesTempMax,
            comarquesTempMin,
        } = this.props;
        
        const { comarcaId } = this.state;        
        const est_cel = {
            1: [
                {
                    valor: comarquesCel[comarcaId - 1].simbol,
                },
            ],
        };
        const prob_prec = {
            1: [
                {
                    valor: (comarquesPrecipitacio[comarcaId - 1].probabilitat).replaceAll('%', ' %').replace('-', ' - ').replace('<', '< ').replace('>', '> '),
                },
            ],
        };

        const temp_max = {
            1: [
                {
                    valor: comarquesTempMax[comarcaId - 1].temperatura,
                },
            ],
        };

        const temp_min = {
            1: [
                {
                    valor: comarquesTempMin[comarcaId - 1].temperatura,
                },
            ],
        };

        const dayBoxData = {
            est_cel,
            prob_prec,
            temp_max,
            temp_min,
        };

        this.setState({ dayBoxData, isDayBoxActive: true });
    };

    calculateZoom = () => {
        let zoom = DEFAULT_ZOOM;
        let w = window.innerWidth;
        let h = window.innerHeight;

        if (document.querySelector('.one-map') && this.mapContainer.current) {
            const headerHeight = 150;
            // * 2 because it only applies to one map widget
            w = this.mapContainer.current.offsetWidth * 1.8;
            h = this.mapContainer.current.offsetHeight * 2 + headerHeight;
        }

        if (this.props.preview) return zoom;
        if (w > 1300 && h > 730) return 8;
        if (w > 700 && h > 450) return 7;
        if (w > 271 && h > 230) return 6.5;
        if (w < 272) return 6;

        return zoom;
    };

    isNotSeaLayer = IDComarca => IDComarca < FIRST_ID_SEA_LAYER;
}



// WEBPACK FOOTER //
// ./src/partials/GeneralPrediction/GeneralPrediction.js